import type { DoctorShift } from '../../types/doctorShift';
import { isValidTimeFormat, getDurationMinutes, createTimeRange } from './TimeRange';

/**
 * バリデーション結果の型
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * シフトバリデーションポリシー
 *
 * @description
 * 医師シフトのビジネスルールに基づくバリデーションを提供する。
 * Domain層の純粋な関数として実装。
 */

/**
 * シフトデータのバリデーションを実行する
 *
 * @param {Partial<DoctorShift>} shift - 検証するシフトデータ
 * @returns {ValidationResult} バリデーション結果
 *
 * @example
 * const result = validateShift({ doctor_id: '1', shift_date: '2025-01-15', ... });
 * if (!result.isValid) {
 *   console.error(result.errors);
 * }
 */
export const validateShift = (shift: Partial<DoctorShift>): ValidationResult => {
  const errors: string[] = [];

  // 必須フィールドチェック
  if (!shift.doctor_id) {
    errors.push('医師を選択してください。');
  }
  if (!shift.shift_date) {
    errors.push('日付を選択してください。');
  }
  if (!shift.start_time) {
    errors.push('開始時間を入力してください。');
  }
  if (!shift.end_time) {
    errors.push('終了時間を入力してください。');
  }

  // 時間形式チェック
  if (shift.start_time && !isValidTimeFormat(shift.start_time)) {
    errors.push('開始時間の形式が不正です。HH:mm形式で入力してください。');
  }
  if (shift.end_time && !isValidTimeFormat(shift.end_time)) {
    errors.push('終了時間の形式が不正です。HH:mm形式で入力してください。');
  }

  // 時間範囲チェック
  if (
    shift.start_time &&
    shift.end_time &&
    isValidTimeFormat(shift.start_time) &&
    isValidTimeFormat(shift.end_time)
  ) {
    const range = createTimeRange(shift.start_time, shift.end_time);
    const duration = getDurationMinutes(range);

    if (duration <= 0) {
      errors.push('終了時間は開始時間より後に設定してください。');
    }
    if (duration > 0 && duration < 30) {
      errors.push('シフトは最低30分以上必要です。');
    }
  }

  // 日付形式チェック
  if (shift.shift_date && !/^\d{4}-\d{2}-\d{2}$/.test(shift.shift_date)) {
    errors.push('日付の形式が不正です。YYYY-MM-DD形式で入力してください。');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * 過去日付かどうかをチェックする
 *
 * @param {string} date - 日付文字列 (YYYY-MM-DD)
 * @returns {boolean} 過去日付の場合はtrue
 */
export const isPastDate = (date: string): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(date);
  return targetDate < today;
};

/**
 * 診療時間範囲内かどうかをチェックする
 *
 * @param {string} time - 時間文字列 (HH:mm)
 * @param {number} minHour - 最小時間（デフォルト: 7）
 * @param {number} maxHour - 最大時間（デフォルト: 22）
 * @returns {boolean} 範囲内の場合はtrue
 */
export const isWithinClinicHours = (
  time: string,
  minHour: number = 7,
  maxHour: number = 22
): boolean => {
  const [hour] = time.split(':').map(Number);
  return hour >= minHour && hour <= maxHour;
};

/**
 * シフトが編集可能かどうかをチェックする
 *
 * @param {DoctorShift} shift - シフトデータ
 * @returns {boolean} 編集可能な場合はtrue
 */
export const isShiftEditable = (shift: DoctorShift): boolean => {
  return !isPastDate(shift.shift_date);
};

/**
 * シフトが削除可能かどうかをチェックする
 *
 * @param {DoctorShift} shift - シフトデータ
 * @returns {boolean} 削除可能な場合はtrue
 */
export const isShiftDeletable = (shift: DoctorShift): boolean => {
  return !isPastDate(shift.shift_date);
};
